import classNames from 'classnames';
import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    Card,
    Exercise,
    LabelBody,
    LabelDisplay1,
    LabelDisplay2,
    ProgressBar,
    SecondaryButton,
    Spacer,
    Textarea,
} from '../../components';
import {
    IllustrationBulb,
    IllustrationControlBackgroundCircles,
    IllustrationControlEnvironment,
    IllustrationControlRelations,
    IllustrationPenPaper,
    Module3S1Exercise3Illustration1,
    Module3S1Exercise3Illustration2,
} from '../../icons';
import { ExerciseStatus } from '../../interfaces';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import { ExerciseData, ExerciseViewProps } from '../exercises';

const page5TextInputKey = 'exercise313Page5TextInputKey';

export const exerciseData313: ExerciseData = {
    ExerciseView: Module3S1Exercise3,
    dataKeys: [page5TextInputKey],
    questions: [
        createQuestionFromConfiguration({
            title: 'Refleksion',
            description:
                'Hvordan var det at udfylde? Gav det nogle nye perspektiver på din situation?',
            dataKeys: [page5TextInputKey],
        }),
    ],
};

const widthConstraint = 'container-in-app';

export function Module3S1Exercise3({
    exercise,
    onContinue,
}: ExerciseViewProps) {
    // For these pages, we need to take control of the max-width of the page
    const isOwningFullWidth =
        exercise.CurrentPage == 3 || exercise.CurrentPage == 4;
    return (
        <Exercise title="Kontrolcirkler" fullWidth={isOwningFullWidth}>
            <ProgressBar
                className={widthConstraint}
                current={exercise.CurrentPage}
                total={6}
            />
            <Page />
        </Exercise>
    );

    function Page() {
        switch (exercise.CurrentPage) {
            case 1:
                return (
                    <Page1
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 2,
                            });
                        }}
                    />
                );
            case 2:
                return (
                    <Page2
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 3,
                            });
                        }}
                    />
                );
            case 3:
                return (
                    <Page3
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 4,
                            });
                        }}
                    />
                );
            case 4:
                return (
                    <Page4
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 5,
                            });
                        }}
                    />
                );
            case 5:
                return (
                    <Page5
                        textInput={exercise.Data[page5TextInputKey] ?? ''}
                        onContinue={(text) => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 6,
                                data: {
                                    [page5TextInputKey]: normalizeInput(text),
                                },
                            });
                        }}
                    />
                );
            case 6:
                return (
                    <Page6
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.Completed,
                                goToNext: false,
                            });
                        }}
                    />
                );
        }
    }
}

type PageProps = {
    onContinue: () => void;
};

function Page1({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2 class="mb-10">
                Hvad kan du kontrollere?
            </LabelDisplay2>
            <LabelBody>
                Som mennesker har vi tendens til at fokusere på alt det, der
                ligger udenfor vores kontrol. Når vi oveni det bekymrer os og
                tænker meget, kan en negativ spiral virkelig sætte sig fast. Det
                kan gøre os mere anspændte og fastlåste. En kontrolcirkel er et
                redskab, der kan hjælpe dig med at skelne mellem det, du kan
                påvirke, og det du ikke kan.
            </LabelBody>
            <Module3S1Exercise3Illustration1 className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>Fortsæt</SecondaryButton>
        </>
    );
}

function Page2({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2 class="mb-10">Før du går i gang</LabelDisplay2>
            <LabelBody>
                Hent noget papir og en blyant. Det skal du bruge til øvelsen.
            </LabelBody>
            <Module3S1Exercise3Illustration2 className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                ok, jeg er klar
            </SecondaryButton>
        </>
    );
}

function Page3({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2 class={classNames(widthConstraint, 'mb-10')}>
                Reflekter over din dag
            </LabelDisplay2>
            <LabelBody class={classNames(widthConstraint, 'mb-30')}>
                Identificér hvilke situationer du har kunnet kontrollere, og
                hvilke der har ligget udenfor din kontrol. Tegn din egen
                kontrolcirkel og fordel situationerne og handlingerne deri.
                <br />
                Her er nogle eksempler:
            </LabelBody>
            <div className="relative">
                {/*
                 * this class-soup allows the illustration to fill the relative
                 * container and clip instead of resizing for smaller screens
                 */}
                <div className="absolute inset-[-20px] top-0 flex justify-center overflow-hidden">
                    <IllustrationControlBackgroundCircles className="absolute" />
                </div>
                <div
                    className={classNames(
                        widthConstraint,
                        'relative flex flex-col',
                    )}
                >
                    <IllustrationControlEnvironment className="mx-auto my-30" />
                    <Spacer />
                    <SecondaryButton onClick={onContinue}>
                        Fortsæt
                    </SecondaryButton>
                </div>
            </div>
        </>
    );
}

function Page4({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2 class={classNames(widthConstraint, 'mb-10')}>
                Hvornår fylder tankemylderet?
            </LabelDisplay2>
            <LabelBody class={classNames(widthConstraint, 'mb-30')}>
                Tænk nu på din generelle situation og de relationer, hvor
                bekymringerne og tankemylderet kan fylde meget. Tegn din egen
                kontrolcirkel. Overvej, hvad der generelt ligger indenfor din
                kontrol, og hvad du ikke har indflydelse på.
            </LabelBody>
            <div className="relative">
                {/*
                 * this class-soup allows the illustration to fill the relative
                 * container and clip instead of resizing for smaller screens
                 */}
                <div className="absolute inset-[-20px] top-0 flex justify-center overflow-hidden">
                    <IllustrationControlBackgroundCircles className="absolute" />
                </div>
                <div
                    className={classNames(
                        widthConstraint,
                        'relative flex flex-col',
                    )}
                >
                    <IllustrationControlRelations className="mx-auto my-30" />
                    <Spacer />
                    <SecondaryButton onClick={onContinue}>
                        Fortsæt
                    </SecondaryButton>
                </div>
            </div>
        </>
    );
}

type Page5Props = {
    textInput: string;
    onContinue: (text: string) => void;
};

function Page5({ textInput, onContinue }: Page5Props) {
    const [text, setText] = useSyncedState(textInput);

    return (
        <>
            <Card class="mb-30">
                <IllustrationPenPaper />
                <LabelDisplay1 class="mb-10 mt-20">Refleksion</LabelDisplay1>
                <LabelBody>
                    Hvordan var det at udfylde? Gav det nogle nye perspektiver
                    på din situation?
                </LabelBody>
                <Textarea
                    className="mt-20"
                    text={text}
                    onChangeText={setText}
                />
                <LabelBody class="mt-10">
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton
                onClick={() => {
                    onContinue(text);
                }}
            >
                Fortsæt
            </SecondaryButton>
        </>
    );
}

function Page6({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2 class="mb-10">Et godt redskab</LabelDisplay2>
            <LabelBody>
                Ved at fokusere på de områder du selv har indflydelse på, kan du
                få en større fornemmelse af kontrol. Det giver dig et fokus på
                det, du kan kontrollere og ro på overtænkningen over det, du
                ikke kan gøre noget ved.
                <br />
                <br />
                Redskabet kan du tage frem løbende igennem dit liv, fordi det
                kan hjælpe dig med at få større overblik over de situationer, du
                kan synes svære at navigere i.
            </LabelBody>
            <IllustrationBulb className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Afslut øvelsen
            </SecondaryButton>
        </>
    );
}
